import { type Deactivatable } from '@/infra/activation'
import { action, computed, observable } from 'mobx'
import { EmailConfirmationApi } from '@/features/email-confirmation/api/email-confirmation-api'
import { filter, interval, map, switchMap } from 'rxjs'
import { takeWhile, tap } from 'rxjs/operators'
import { jwtDecode } from 'jwt-decode'
import { type SnackbarFactory } from '@/features/shell'
import { toStream } from '@/utils/mobx-util'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * The payload for the session token.
 */
interface SessionTokenPayload {
  iat: number
  exp: number
  email: string
}

/**
 * View Model for Email Confirmation.
 */
export class ConfirmEmailViewModel implements Deactivatable {
  /**
   * Lifecycle tracking.
   */
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The token payload.
   */
  @observable
  private accessor _tokenPayload: SessionTokenPayload | null = null

  /**
   * Indicates whether the user's email has been confirmed.
   */
  @observable
  accessor emailConfirmed = false

  /**
   * Indicates whether this confirmation attempt has expired based on the token.
   */
  @observable
  accessor pageExpired = false

  /**
   * Fires when navigation to the email confirmed page should occur.
   */
  navigateToEmailConfirmed$ = toStream(() => this.emailConfirmed).pipe(
    filter((confirmed) => confirmed),
  )

  /**
   * Email Confirmation API.
   */
  private _api = new EmailConfirmationApi()

  /**
   * The session token.
   */
  private _token: string | null = null

  /**
   * Clock that tickets every 5 second once the token payload is provided.
   */
  private readonly _timer = toStream(() => this._tokenPayload, true).pipe(
    filter(Boolean),
    map((payload) => payload.exp * 1000),
    switchMap((exp) =>
      interval(5000).pipe(
        takeWhile(() => !this.emailConfirmed && !this.pageExpired),
        tap(() => this.checkTokenExpiration(exp)),
        tap(() => this.checkVerificationStatus()),
      ),
    ),
  )

  constructor(private readonly _newSnackbar: SnackbarFactory) {
    this._lifecycle.manage(this._timer)
  }

  /**
   * The user's email, based on the token.
   */
  @computed
  get email() {
    return this._tokenPayload?.email ?? null
  }

  /**
   * Activates the view model.
   */
  @action.bound
  activate(token: string) {
    this._lifecycle.activate()
    this._token = token

    const { email, exp, iat } = decodeToken(token)
    if (!email || !exp || !iat) {
      this._newSnackbar()
        .saying('Invalid token')
        .type('error')
        .autoDismiss()
        .show()
      return
    }

    this._tokenPayload = { email, exp, iat }
  }

  /**
   * @inheritDoc
   */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Resends the verification email.
   */
  resendVerificationEmail = async () => {
    if (!this._token) {
      return
    }

    await this._api
      .resendEmail(this._token)
      .catch(() =>
        this._newSnackbar()
          .saying('An error occurred')
          .type('error')
          .autoDismiss()
          .show(),
      )
  }

  /**
   * Checks the verification status of the user. Used to automatically update the page.
   */
  @action.bound
  checkVerificationStatus() {
    if (!this._token) {
      return
    }

    void this._api.getVerificationStatus(this._token).then(
      action((status) => {
        this.emailConfirmed = status
      }),
    )
  }

  /**
   * Checks whether the token has expired.
   */
  @action.bound
  checkTokenExpiration(expiresAt: number) {
    if (Date.now() > expiresAt) {
      this.pageExpired = true
    }
  }
}

/**
 * Decodes the provided token.
 */
function decodeToken(token: string): Partial<SessionTokenPayload> {
  try {
    const decoded = jwtDecode<SessionTokenPayload>(token)
    return {
      email: decoded.email ?? undefined,
      exp: decoded.exp ?? undefined,
      iat: decoded.iat ?? undefined,
    }
  } catch {
    return {
      email: undefined,
      exp: undefined,
      iat: undefined,
    }
  }
}
